import React from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";

type Props = {
  /** "YYYY-MM-DD" or an ISO timestamp; "" when unknown. */
  value: string;
  /** "YYYY-MM-DD", or "" when cleared. Not called while a date is half typed. */
  onChange: (value: string) => void;
  disabled?: boolean;
};

const pad = (n: number) => String(n).padStart(2, "0");

// Only the date part counts: "1990-05-10T00:00:00.000Z" is 10 May 1990
// wherever the browser is, never 9 May west of UTC.
const toDate = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || "");
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const toValue = (date: Date) =>
  `${String(date.getFullYear()).padStart(4, "0")}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const oldest = new Date(new Date().getFullYear() - 120, 0, 1);

// While a year is being typed the picker reports 0001, 0019, 0198... Only a
// whole, believable date is passed on.
const isComplete = (date: Date) => !Number.isNaN(date.getTime()) && date >= oldest && date <= new Date();

/**
 * A date of birth: type it as DD/MM/YYYY, or pick it. The calendar opens on
 * the year, so going back decades is one click, not hundreds of months.
 */
const BirthDateField: React.FC<Props> = ({ value, onChange, disabled }) => {
  // Kept apart from `value` so a date being typed is not wiped mid-way.
  const [draft, setDraft] = React.useState<Date | null>(() => toDate(value));

  // Follow outside changes (the record loading, a save) but not the echo of
  // our own onChange, which would reset the field while it is being typed in.
  React.useEffect(() => {
    setDraft((current) => {
      const next = toDate(value);
      if (current && next && toValue(current) === toValue(next)) return current;
      if (!next && current && !isComplete(current)) return current;
      return next;
    });
  }, [value]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <DatePicker
        value={draft}
        disabled={disabled}
        disableFuture
        minDate={oldest}
        openTo="year"
        views={["year", "month", "day"]}
        format="dd/MM/yyyy"
        onChange={(date) => {
          setDraft(date);
          if (!date) onChange("");
          else if (isComplete(date)) onChange(toValue(date));
        }}
        slotProps={{
          field: { clearable: true },
          textField: {
            fullWidth: true,
            inputProps: { "aria-label": "Date of birth" },
            sx: {
              // Same look as CustomTextField.
              "& .MuiPickersInputBase-root, & .MuiOutlinedInput-root": {
                borderRadius: "12px",
                height: "40px",
              },
              "& .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline": {
                borderColor: "#ccc",
              },
              "& .MuiPickersInputBase-root:hover .MuiPickersOutlinedInput-notchedOutline": {
                borderColor: "#868585",
              },
              "& .MuiPickersInputBase-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
                borderColor: "#868585",
                borderWidth: "1px",
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default BirthDateField;
